import React, { useEffect, useMemo, useRef } from "react";
import { useQuery } from "react-query";
import { QUERIES } from "../../../config/apiConfig";
import axios from "../../../config/axiosInstance";
import DrawerLoading from "../../DrawerLoading";
import PositivoPlusCNPJQuery from "../../queries/PositivoPlusCNPJQuery";
import PositivoPlusCPFQuery from "../../queries/PositivoPlusCPFQuery";
import ScrBacenScoreQuery from "../../queries/ScrBacenScoreQuery";
import CompletaPlusCNPJQuery from "../../queries/CompletaPlusCNPJQuery";
import CompletaPlusCPFQuery from "../../queries/CompletaPlusCPFQuery";

const queries = [
  {
    id: 1,
    name: "Completa Plus CNPJ",
    component: <CompletaPlusCNPJQuery />,
  },
  {
    id: 2,
    name: "Completa Plus CPF",
    component: <CompletaPlusCPFQuery />,
  },
  {
    id: 5,
    name: "Sim Scr + Score",
    component: <ScrBacenScoreQuery />,
  },
  {
    id: 3,
    name: "Positivo Plus CNPJ",
    component: <PositivoPlusCNPJQuery />,
  },
  {
    id: 4,
    name: "Essencial Plus CPF",
    component: <PositivoPlusCPFQuery />,
  },
];

const parseMaybeJson = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (error) {
      console.error("Erro ao converter dados da consulta:", error);
      return null;
    }
  }
  return value;
};

const getQueryId = (candidate) =>
  candidate?.QueryId ?? candidate?.queryId ?? candidate?.Query?.id ?? null;

const getQueryName = (candidate) =>
  candidate?.queryName ?? candidate?.Query?.name ?? candidate?.name ?? null;

const normalizeQueryData = (queryId, data) => {
  if (!data) return data;

  let normalized = data;

  if (!normalized.consultaCredito && normalized.crednet?.consultaCredito) {
    normalized = {
      ...normalized,
      consultaCredito: normalized.crednet.consultaCredito,
    };
  }

  if (
    !normalized.protestosDirCartorio &&
    Array.isArray(normalized.protestoNacional?.ocorrencias)
  ) {
    normalized = {
      ...normalized,
      protestosDirCartorio: normalized.protestoNacional.ocorrencias,
    };
  }

  if (!normalized.defineLimite && normalized.defineLimitePositivo) {
    normalized = {
      ...normalized,
      ...normalized.defineLimitePositivo,
    };
  }

  return normalized;
};

const SelectedQueryReview = ({ query, dataFromLocalStorage, onReady }) => {
  const localData = useMemo(
    () => parseMaybeJson(dataFromLocalStorage ?? query?.data),
    [dataFromLocalStorage, query?.data]
  );

  const {
    isLoading,
    error,
    data: queryData,
  } = useQuery(
    ["queryData", query?.id],
    async () => {
      const { data } = await axios.get(
        `${QUERIES.GET_USER_QUERY}/${query?.id}`
      );

      return data.data;
    },
    {
      enabled: !!query?.id && !localData,
    }
  );

  const remoteData = useMemo(
    () => parseMaybeJson(queryData?.data),
    [queryData?.data]
  );

  const resolvedData = localData ?? remoteData;
  const resolvedQueryId = getQueryId(queryData) ?? getQueryId(query);
  const resolvedQueryName =
    getQueryName(query) ?? getQueryName(queryData) ?? null;
  const resolvedUserQueryId = query?.id ?? queryData?.id ?? null;
  const normalizedData = useMemo(
    () => normalizeQueryData(resolvedQueryId, resolvedData),
    [resolvedData, resolvedQueryId]
  );
  const lastReadyRef = useRef(null);

  useEffect(() => {
    lastReadyRef.current = null;
  }, [query?.id, dataFromLocalStorage]);

  useEffect(() => {
    if (!onReady) return;
    if (isLoading) return;
    if (!resolvedQueryId || !normalizedData) return;
    if (lastReadyRef.current === resolvedQueryId) return;

    lastReadyRef.current = resolvedQueryId;
    onReady();
  }, [isLoading, onReady, resolvedQueryId, normalizedData]);

  const getComponentByQueryId = (queryId) => {
    const queryMatch = queries.find((q) => q.id === queryId);
    if (!queryMatch) return <div>Consulta não reconhecida</div>;

    const componentKey = `${resolvedUserQueryId ?? "unknown"}-${
      normalizedData ? "ready" : "empty"
    }`;

    return (
      <React.Fragment key={componentKey}>
        {React.cloneElement(queryMatch.component, {
          dataFromLocalStorage: normalizedData, // se o componente usar
          query: {
            id: queryId,
            name: resolvedQueryName ?? queryMatch.name,
          },
        })}
      </React.Fragment>
    );
  };

  return (
    <div className="relative">
      {resolvedQueryId && getComponentByQueryId(resolvedQueryId)}
      {!isLoading && !error && !resolvedQueryId && (
        <div className="text-sm text-slate-500">Consulta não reconhecida.</div>
      )}
      {!isLoading && !error && resolvedQueryId && !normalizedData && (
        <div className="text-sm text-slate-500">
          Nenhum dado disponível para essa consulta.
        </div>
      )}
      {error && (
        <div className="text-sm text-red-600">
          Não foi possível carregar a consulta.
        </div>
      )}
      {isLoading && <DrawerLoading />}
    </div>
  );
};

export default SelectedQueryReview;
